import React, { useState, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import HelpIcon from '../../shared/HelpIcon';
import SvgExclamationPointIcon from '../../svg/ExclamationPointIcon';
import InlineHelpText from '../../shared/InlineHelpText';
import AccessLevelsLabels from '../../shared/AccessLevelsLabels';
import UserDisplayModes from '../../shared/UserDisplayModes';
import SvgCheckMarkIcon from '../../svg/CheckMarkIcon';
import UserDashboardButtonsDisplay from './UserDashboardButtonsDisplay';
import CategoryDisplayItemsDisplay from './CategoryDisplayItemsDisplay';
import AccessControl from '../../../AccessControl';
import permissions from '../../../permissionsList';
import { staffAccessLevelsDescriptions } from '../../shared/SharedInternationalizations';
import AlertGroupsPermissionDisplay from './AlertGroupsPermissionDisplay';

function UserStaffProfile(props) {
  const {
    currentLocation,
    userObj,
    staffPreferences,
    alertDeviceAccess,
  } = props;
  
  const {
    user,
    checkin_reports,
    refresh_rate,
    show_panic_buttons,
    panic_title,
    disallow_web_alarm_confirm,
    display_mode,
    admin_reports,
    access_level,
    edit_status_checkin_exclusion,
  } = staffPreferences || {};
  const is_active = userObj.is_active;
  const [showAccessLevelHelpText, setShowAccessLevelHelpText] = useState(false);
  function toggleAccessLevelHelpText(value) {
    setShowAccessLevelHelpText(value);
  }
  const accessLevelHelpTextRef = useRef();
  const [showDisplayModeHelpText, setShowDisplayModeHelpText] = useState(false);
  function toggleDisplayModeHelpText(value) {
    setShowDisplayModeHelpText(value);
  }
  const displayModeHelpTextRef = useRef();
  const displayModeHelpText = (
    <FormattedMessage
      id="userCheckinPreferences.displayModeHelpText"
      defaultMessage="This is the display mode for this user."
      description="Help text for understanding what the access level is."
    />
  );

  const [
    showDailyAdminReportHelpText,
    setShowDailyAdminReportHelpText,
  ] = useState(false);
  function toggleShowDailyAdminReportHelpText(value) {
    setShowDailyAdminReportHelpText(value);
  }
  const dailyAdminReportHelpTextRef = useRef();
  const dailyAdminReportHelpText = (
    <FormattedMessage
      id="userCheckinPreferences.dailyAdminReportHelpText"
      defaultMessage="The daily admin report is a report which also includes low battery alarms."
      description="Help text for understanding what the user check-in exclude means."
    />
  );

  const [
    showRestrictConfirmationOfAlarmsHelpText,
    setRestrictConfirmationOfAlarmsHelpText,
  ] = useState(false);
  function toggleShowResctrictConfirmationOfAlarmsHelpText(value) {
    setRestrictConfirmationOfAlarmsHelpText(value);
  }
  const restrictConfirmationOfAlarmsHelpTextRef = useRef();
  const restrictConfirmationOfAlarmsHelpText = (
    <FormattedMessage
      id="userCheckinPreferences.restrictConfirmationOfAlarmsHelpText"
      defaultMessage="While most users can view alarms on the SARA dashboard, not all can confirm alarms through that interface. This indicates if this user is or is not allowed to do so."
    />
  );

  const [
    showCheckInReportsEmailHelpText,
    setShowCheckInReportsEmailHelpText,
  ] = useState(false);
  function toggleShowCheckInReportsEmailHelpText(value) {
    setShowCheckInReportsEmailHelpText(value);
  }
  const checkInReportsEmailHelpTextRef = useRef();
  const checkInReportsEmailHelpText = (
    <FormattedMessage
      id="userCheckinPreferences.checkInReportsEmailHelpText"
      defaultMessage="Check-in reports are reports of which users that are supposed to check in to SARA have or have not done so."
    />
  );

  const [
    showEditStatusCheckinExclusionHelpText,
    setShowEditStatusCheckinExclusionHelpText,
  ] = useState(false);
  function toggleEditStatusCheckinExclusionHelpText(value) {
    setShowEditStatusCheckinExclusionHelpText(value);
  }
  const editStatusCheckinExclusionHelpTextRef = useRef();
  const editStatusCheckinExclusionHelpText = (
    <FormattedMessage
      id="userCheckinPreferences.editStatusCheckinExclusionHelpText"
      defaultMessage="This user is allowed to edit User Status and check-in exclusion settings."
    />
  );

  const [
    showPanicButtonTitleHelpText,
    setShowPanicButtonTitleHelpText,
  ] = useState(false);
  function togglePanicButtonTitleHelpText(value) {
    setShowPanicButtonTitleHelpText(value);
  }
  const panicButtonTitleHelpTextRef = useRef();
  const panicButtonTitleHelpText = (
    <FormattedMessage
      id="userCheckinPreferences.panicButtonTitleHelpText"
      defaultMessage="The label on the dashboard panic button for this user."
    />
  );

  const [showPanicButtonsHelpText, setShowPanicButtonsHelpText] = useState(
    false
  );
  function toggleShowPanicButtonsHelpText(value) {
    setShowPanicButtonsHelpText(value);
  }
  const panicButtonsHelpTextRef = useRef();
  const panicButtonsHelpText = (
    <FormattedMessage
      id="userCheckinPreferences.panicButtonsHelpText"
      defaultMessage="On the dashboard view there are optional panic buttons that can be used to trigger alarms."
    />
  );

  const [showRefreshRateHelpText, setShowRefreshRateHelpText] = useState(false);
  function toggleRefreshRateHelpText(value) {
    setShowRefreshRateHelpText(value);
  }
  const refreshRateHelpTextRef = useRef();
  const refreshRateHelpText = (
    <FormattedMessage
      id="userCheckinPreferences.refreshRateHelpText"
      defaultMessage="This is how frequent, in seconds, the dashboard refreshes for the user."
    />
  );

  const dailyAdminReport = (
    <div
      className="content-display__column content-display__column--1-2"
      data-test="call-for-checkin-container">
      <div className="content-display__item-pill-container">
        {admin_reports ? (
          <React.Fragment>
            <div className="item-container__info-container">
              <SvgCheckMarkIcon className="text-success margin-right-10" />
            </div>
            <div className="item-container__main-container">
              <div className="item-container__main-text text-large text-light">
                <FormattedMessage
                  data-test="daily-admin-report-true"
                  id="userCheckinPreferences.dailyAdminReportText"
                  defaultMessage="This user <b>will receive</b> the daily admin report"
                  description="The check-in instructions"
                  values={{
                    b: (msg) => (
                      <span className="item-container__text-highlighted">
                        {msg}
                      </span>
                    ),
                  }}
                />
              </div>
            </div>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <div className="item-container__info-container">
              <SvgExclamationPointIcon className="text-alert margin-right-10" />
            </div>
            <div className="item-container__main-container">
              <div className="item-container__main-text text-large text-light">
                <FormattedMessage
                  data-test="daily-admin-report-false"
                  id="userCheckinPreferences.dailyAdminReportFalseText"
                  defaultMessage="This user <b>will not receive</b> the daily admin report"
                  description="The check-in instructions"
                  values={{
                    b: (msg) => (
                      <span className="item-container__text-highlighted item-container__text-highlighted--alert">
                        {msg}
                      </span>
                    ),
                  }}
                />
              </div>
            </div>
          </React.Fragment>
        )}
        <div className="item-container__info-container">
          <HelpIcon
            styles="margin-left-5"
            onClick={() => dailyAdminReportHelpTextRef.current.toggleHelpText()}
            toggled={showDailyAdminReportHelpText}
          />
        </div>
      </div>
      <InlineHelpText
        ref={dailyAdminReportHelpTextRef}
        toggleFunction={toggleShowDailyAdminReportHelpText}
        message={dailyAdminReportHelpText}
      />
    </div>
  );

  const allowAlarmConfirm = (
    <div
      className="content-display__column content-display__column--1-2"
      data-test="call-for-checkin-container">
      <div className="content-display__item-pill-container">
        {disallow_web_alarm_confirm ? (
          <React.Fragment>
            <div className="item-container__info-container">
              <SvgExclamationPointIcon className="text-alert margin-right-10" />
            </div>
            <div className="item-container__main-container">
              <div className="item-container__main-text text-large text-light">
                <FormattedMessage
                  data-test="allow-alarm-confirm-true"
                  id="staffProfile.allowAlarmConfirmText"
                  defaultMessage="This user <b>is not allowed</b> to confirm alarms from the SARA dashboard"
                  description="The check-in instructions"
                  values={{
                    b: (msg) => (
                      <span className="item-container__text-highlighted item-container__text-highlighted--alert">
                        {msg}
                      </span>
                    ),
                  }}
                />
              </div>
            </div>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <div className="item-container__info-container">
              <SvgCheckMarkIcon className="text-success margin-right-10" />
            </div>
            <div className="item-container__main-container">
              <div className="item-container__main-text text-large text-light">
                <FormattedMessage
                  data-test="allow-alarm-confirm-false"
                  id="staffProfile.allowAlarmConfirmTextFalse"
                  defaultMessage="This user <b>is allowed</b> to confirm alarms from the SARA dashboard"
                  description="The check-in instructions"
                  values={{
                    b: (msg) => (
                      <span className="item-container__text-highlighted">
                        {msg}
                      </span>
                    ),
                  }}
                />
              </div>
            </div>
          </React.Fragment>
        )}
        <div className="item-container__info-container">
          <HelpIcon
            styles="margin-left-5"
            onClick={() =>
              restrictConfirmationOfAlarmsHelpTextRef.current.toggleHelpText()
            }
            toggled={showRestrictConfirmationOfAlarmsHelpText}
          />
        </div>
      </div>
      <InlineHelpText
        ref={restrictConfirmationOfAlarmsHelpTextRef}
        toggleFunction={toggleShowResctrictConfirmationOfAlarmsHelpText}
        message={restrictConfirmationOfAlarmsHelpText}
      />
    </div>
  );

  const checkinReports = (
    <div
      className="content-display__column content-display__column--1-2"
      data-test="call-for-checkin-container">
      <div className="content-display__item-pill-container">
        {checkin_reports ? (
          <React.Fragment>
            <div className="item-container__info-container">
              <SvgCheckMarkIcon className="text-success margin-right-10" />
            </div>
            <div className="item-container__main-container">
              <div className="item-container__main-text text-large text-light">
                <FormattedMessage
                  data-test="check-in-email-true"
                  id="userCheckinPreferences.checkInReportsEmailText"
                  defaultMessage="This user <b>will receive</b> check-in report emails"
                  description="The check-in instructions"
                  values={{
                    b: (msg) => (
                      <span className="item-container__text-highlighted">
                        {msg}
                      </span>
                    ),
                  }}
                />
              </div>
            </div>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <div className="item-container__info-container">
              <SvgExclamationPointIcon className="text-alert margin-right-10" />
            </div>
            <div className="item-container__main-container">
              <div className="item-container__main-text text-large text-light">
                <FormattedMessage
                  data-test="check-in-email-false"
                  id="userCheckinPreferences.checkInReportsEmailFalseText"
                  defaultMessage="This user <b>will not receive</b> check-in report emails"
                  description="The check-in instructions"
                  values={{
                    b: (msg) => (
                      <span className="item-container__text-highlighted item-container__text-highlighted--alert">
                        {msg}
                      </span>
                    ),
                  }}
                />
              </div>
            </div>
          </React.Fragment>
        )}
        <div className="item-container__info-container">
          <HelpIcon
            styles="margin-left-5"
            onClick={() =>
              checkInReportsEmailHelpTextRef.current.toggleHelpText()
            }
            toggled={showCheckInReportsEmailHelpText}
          />
        </div>
      </div>
      <InlineHelpText
        ref={checkInReportsEmailHelpTextRef}
        toggleFunction={toggleShowCheckInReportsEmailHelpText}
        message={checkInReportsEmailHelpText}
      />
    </div>
  );

  const showPanicButtons = (
    <div
      className="content-display__column content-display__column--1-2"
      data-test="call-for-checkin-container">
      <div className="content-display__item-pill-container">
        {show_panic_buttons ? (
          <React.Fragment>
            <div className="item-container__info-container">
              <SvgCheckMarkIcon className="text-success margin-right-10" />
            </div>
            <div className="item-container__main-container">
              <div className="item-container__main-text text-large text-light">
                <FormattedMessage
                  data-test="panic-button-true"
                  id="userCheckinPreferences.showPanicButtonsText"
                  defaultMessage="This user <b>will see</b> dashboard panic buttons"
                  description="The check-in instructions"
                  values={{
                    b: (msg) => (
                      <span className="item-container__text-highlighted">
                        {msg}
                      </span>
                    ),
                  }}
                />
              </div>
            </div>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <div className="item-container__info-container">
              <SvgExclamationPointIcon className="text-alert margin-right-10" />
            </div>
            <div className="item-container__main-container">
              <div className="item-container__main-text text-large text-light">
                <FormattedMessage
                  data-test="panic-button-false"
                  id="userCheckinPreferences.showPanicButtonsFalseText"
                  defaultMessage="This user <b>will not see</b> dashboard panic buttons"
                  description="The check-in instructions"
                  values={{
                    b: (msg) => (
                      <span className="item-container__text-highlighted">
                        {msg}
                      </span>
                    ),
                  }}
                />
              </div>
            </div>
          </React.Fragment>
        )}
        <div className="item-container__info-container">
          <HelpIcon
            styles="margin-left-5"
            onClick={() => panicButtonsHelpTextRef.current.toggleHelpText()}
            toggled={showPanicButtonsHelpText}
          />
        </div>
      </div>
      <InlineHelpText
        ref={panicButtonsHelpTextRef}
        toggleFunction={toggleShowPanicButtonsHelpText}
        message={panicButtonsHelpText}
      />
    </div>
  );

  const panicButtonTitle = show_panic_buttons ? (
    <div
      className="content-display__column content-display__column--1-2"
      data-test="call-for-checkin-container">
      <div className="content-display__item-pill-container">
        <div className="item-container__main-container">
          <div className="item-container__main-text text-large text-light">
            <FormattedMessage
              id="userDisplay.panicButtonTitleText"
              defaultMessage="Panic Button Title"
              description="The check-in instructions"
            />
          </div>
          <div
            className="item-container__secondary-text text-medium text-bold"
            data-test="individual-panic-button-title">
            {panic_title}
          </div>
        </div>
        <div className="item-container__info-container">
          <HelpIcon
            styles="margin-left-5"
            onClick={() => panicButtonTitleHelpTextRef.current.toggleHelpText()}
            toggled={showPanicButtonTitleHelpText}
          />
        </div>
      </div>
      <InlineHelpText
        ref={panicButtonTitleHelpTextRef}
        toggleFunction={togglePanicButtonTitleHelpText}
        message={panicButtonTitleHelpText}
      />
    </div>
  ) : null;
  
  
   const editStatusCheckInAccess = ["1", "4", "6", "8", 1, 4, 6, 8].includes(access_level) ? (
    <div
      className="content-display__column content-display__column--1-2"
      data-test="call-for-checkin-container">
      <div className="content-display__item-pill-container">
        {edit_status_checkin_exclusion ? (
          <React.Fragment>
            <div className="item-container__info-container">
              <SvgCheckMarkIcon className="text-success margin-right-10" />
            </div>
            <div className="item-container__main-container">
              <div className="item-container__main-text text-large text-light">
                <FormattedMessage
                  data-test="edit-status-checkin-true"
                  id="userStaffProfileOptions.statusCheckinOptionText"
                  defaultMessage="This user <b>will be able to edit </b> a User Status and check-in exclusion settings "
                  description="The user status check-in exclusion instructions"
                  values={{
                    b: (msg) => (
                      <span className="item-container__text-highlighted">
                        {msg}
                      </span>
                    ),
                  }}
                />
              </div>
            </div>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <div className="item-container__info-container">
              <SvgExclamationPointIcon className="text-alert margin-right-10" />
            </div>
            <div className="item-container__main-container">
              <div className="item-container__main-text text-large text-light">
                <FormattedMessage
                  data-test="edit-status-checkin-false"
                  id="userStaffProfileOptions.statusCheckinOptionFalseText"
                  defaultMessage="This user <b>will not be able</b> to edit a User Status and check-in exclusion settings"
                  description="The user status check-in exclusion instructions"
                  values={{
                    b: (msg) => (
                      <span className="item-container__text-highlighted item-container__text-highlighted--alert">
                        {msg}
                      </span>
                    ),
                  }}
                />
              </div>
            </div>
          </React.Fragment>
        )}
        <div className="item-container__info-container">
          <HelpIcon
            styles="margin-left-5"
            onClick={() =>
               editStatusCheckinExclusionHelpTextRef.current.toggleHelpText()
            }
            toggled={showEditStatusCheckinExclusionHelpText}
          />
        </div>
      </div>
      <InlineHelpText
        ref={editStatusCheckinExclusionHelpTextRef}
        toggleFunction={toggleEditStatusCheckinExclusionHelpText}
        message={editStatusCheckinExclusionHelpText}
      />
    </div>
  ) : null;

  const panicButtonList = show_panic_buttons ? (
    <div
      className="content-display__column"
      data-test="call-for-checkin-container">
      <div className="content-display__item-pill-container">
        <div className="item-container__main-container">
          <div className="item-container__main-text text-large text-light">
            <FormattedMessage
              id="userDisplay.additionalPanicButtonsText"
              defaultMessage="Additional Panic Buttons"
              description="The check-in instructions"
            />
          </div>
          <div
            className="item-container__secondary-text text-medium text-bold"
            data-test="panic-buttons-title">
            <UserDashboardButtonsDisplay
              userId={userObj.id}
              data-test="dashboard-buttons-display"
            />
          </div>
        </div>
        <AccessControl
          data-test="access-control"
          feature={permissions.static.users.editUserButtons}>
          <div className="item-container__info-container">
            <FormattedMessage
              id="userStaffProfile.userPanicButtonsEdit"
              defaultMessage="Edit Additional Buttons"
              description="The check-in options"
              data-test="panic-buttons-link-text">
              {(txt) =>
                is_active ? (
                  <Link
                    data-test="panic-buttons-link"
                    to={`/users/${user}/edit/?mode=panicButtons`}
                    state={{
                      modal: true,
                      previous: currentLocation,
                    }}
                    className="link-button button-secondary button-sm text-small">
                    {txt}
                  </Link>
                ) : null
              }
            </FormattedMessage>
            <HelpIcon
              styles="margin-left-5"
              onClick={() =>
                panicButtonTitleHelpTextRef.current.toggleHelpText()
              }
              toggled={showPanicButtonTitleHelpText}
            />
          </div>
        </AccessControl>
      </div>
      <InlineHelpText
        ref={panicButtonTitleHelpTextRef}
        toggleFunction={togglePanicButtonTitleHelpText}
        message={panicButtonTitleHelpText}
      />
    </div>
  ) : null;

  return (
    <div className="content-display__container" data-test="content-container">
      <div className="width-100 flex-spread align-center">
        <FormattedMessage
          id="userStaffPreferences.userStaffOptions"
          defaultMessage="User Staff Options"
          description="The check-in options">
          {(txt) => (
            <span className="text-large text-light no-margin">{txt}: </span>
          )}
        </FormattedMessage>
      </div>
      <div
        className="content-display__column content-display__column--1-2"
        data-test="call-for-checkin-container">
        <div className="content-display__item-pill-container">
          <div className="item-container__main-container">
            <div className="item-container__main-text text-large text-light">
              <FormattedMessage
                id="userCheckinPreferences.accessLevelText"
                defaultMessage="Access Level"
                description="The check-in instructions"
              />
            </div>
            <div className="item-container__secondary-text text-medium text-bold">
              <AccessLevelsLabels
                level={access_level}
                data-test="access-level"
              />
            </div>
          </div>
          <div className="item-container__info-container">
            <HelpIcon
              styles="margin-left-5"
              onClick={() => accessLevelHelpTextRef.current.toggleHelpText()}
              toggled={showAccessLevelHelpText}
            />
          </div>
        </div>
        <InlineHelpText
          ref={accessLevelHelpTextRef}
          toggleFunction={toggleAccessLevelHelpText}
          message={staffAccessLevelsDescriptions}
        />
      </div>
      <div
        className="content-display__column content-display__column--1-2"
        data-test="call-for-checkin-container">
        <div className="content-display__item-pill-container">
          <div className="item-container__main-container">
            <div className="item-container__main-text text-large text-light">
              <FormattedMessage
                id="userCheckinPreferences.displayModeText"
                defaultMessage="Display Mode"
                description="The check-in instructions"
              />
            </div>
            <div className="item-container__secondary-text text-medium text-bold">
              <UserDisplayModes mode={display_mode} data-test="display-mode" />
            </div>
          </div>
          <div className="item-container__info-container">
            <HelpIcon
              styles="margin-left-5"
              onClick={() => displayModeHelpTextRef.current.toggleHelpText()}
              toggled={showDisplayModeHelpText}
            />
          </div>
        </div>
        <InlineHelpText
          ref={displayModeHelpTextRef}
          toggleFunction={toggleDisplayModeHelpText}
          message={displayModeHelpText}
        />
      </div>
      <div
        className="content-display__column content-display__column--1-2"
        data-test="call-for-checkin-container">
        <div className="content-display__item-pill-container">
          <div className="item-container__main-container">
            <div className="item-container__main-text text-large text-light">
              <FormattedMessage
                id="userCheckinPreferences.refreshRateText"
                defaultMessage="Dashboard Refresh Frequency"
                description="The check-in instructions"
              />
            </div>
            <div className="item-container__secondary-text text-medium text-bold">
              <span className="margin-right-5" data-test="refresh-rate">
                {refresh_rate}
              </span>
              <FormattedMessage
                id="userCheckinPreferences.seconds"
                defaultMessage="seconds"
                description="The seconds unit of time"
              />
            </div>
          </div>
          <div className="item-container__info-container">
            <HelpIcon
              styles="margin-left-5"
              onClick={() => refreshRateHelpTextRef.current.toggleHelpText()}
              toggled={showRefreshRateHelpText}
            />
          </div>
        </div>
        <InlineHelpText
          ref={refreshRateHelpTextRef}
          toggleFunction={toggleRefreshRateHelpText}
          message={refreshRateHelpText}
        />
      </div>
      {dailyAdminReport}
      {allowAlarmConfirm}
      {checkinReports}
      {showPanicButtons}
      {panicButtonTitle}
      {panicButtonList}
      {editStatusCheckInAccess}
      {alertDeviceAccess &&
      (alertDeviceAccess.value === '1' || alertDeviceAccess.value === 1) ? (
        <AlertGroupsPermissionDisplay user={userObj} />
      ) : null}
        
      
      <CategoryDisplayItemsDisplay
        user={userObj}
        currentLocation={currentLocation}
      />

      <AccessControl
        data-test="access-control"
        feature={permissions.static.users.editSaraPreferences}
        userToEdit={user}>
        <div className="width-100 flex-spread align-center">
          <div></div>
          <FormattedMessage
            id="userCheckinPreferences.userStaffPreferencesEdit"
            defaultMessage="Edit Staff Preferences"
            description="The check-in options"
            data-test="edit-link-text">
            {(txt) =>
              is_active ? (
                <Link
                  data-test="edit-link"
                  to={`/users/${user}/edit/?mode=staffOptions`}
                  state={{
                    modal: true,
                    previous: currentLocation,
                  }}
                  className="link-button button-secondary button-sm text-small">
                  {txt}
                </Link>
              ) : null}
            
          </FormattedMessage>
        </div>
      </AccessControl>
    </div>
  );
}

export default UserStaffProfile;
